import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react"
import { cn } from "../../lib/cn"
import type { AlertVariant } from "./Alert"

interface ToastItem {
  id: number
  variant: AlertVariant
  message: string
}

interface ToastCtx {
  show: (message: string, variant?: AlertVariant) => void
}

const ToastContext = createContext<ToastCtx>({ show: () => {} })

const variantConfig: Record<AlertVariant, { classes: string; icon: ReactNode }> = {
  info: { classes: "bg-surface text-text-primary border-border-subtle", icon: <Info className="size-5 text-accent" /> },
  success: { classes: "bg-surface text-text-primary border-border-subtle", icon: <CheckCircle2 className="size-5 text-success" /> },
  danger: { classes: "bg-surface text-text-primary border-border-subtle", icon: <AlertCircle className="size-5 text-danger" /> },
  warning: { classes: "bg-surface text-text-primary border-border-subtle", icon: <AlertCircle className="size-5 text-accent" /> },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const show = useCallback((message: string, variant: AlertVariant = "info") => {
    const id = Date.now() + Math.random()
    setToasts((prev) => [...prev, { id, message, variant }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 4000)
  }, [])

  const dismiss = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id))

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-lg z-50 flex flex-col items-center gap-sm px-lg">
        {toasts.map((t) => {
          const config = variantConfig[t.variant]
          return (
            <div
              key={t.id}
              role="status"
              className={cn(
                "pointer-events-auto flex w-full max-w-[384px] items-center gap-sm rounded-box border px-md py-sm shadow-modal",
                config.classes,
              )}
            >
              {config.icon}
              <p className="flex-1 text-body-sm">{t.message}</p>
              <button
                type="button"
                aria-label="إغلاق"
                onClick={() => dismiss(t.id)}
                className="shrink-0 rounded-button p-2xs hover:bg-surface-muted"
              >
                <X className="size-4" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
